import type { AppServices } from '@dcc/rules/app/services.ts';
import type { Character } from '@dcc/rules/domain/character.ts';
import type { RuntimeState } from '@dcc/rules/domain/runtime.ts';
import type { Item } from '@dcc/rules/content/types.ts';
import type { StatId } from '@dcc/rules/domain/stats.ts';
import type { DamageType } from '@dcc/rules/rules/damage.ts';
import type { CustomSkillDraft } from '@dcc/rules/rules/customSkills.ts';
import { buildCustomSkillDefinition, checkCustomSkillDraft } from '@dcc/rules/rules/customSkills.ts';
import { contentRegistry } from '@dcc/rules/content/dccCoreContent.ts';
import { createCustomItem } from '@dcc/rules/app/customItemService.ts';
import { createCustomSkill } from '@dcc/rules/app/customSkillService.ts';
import { addItem } from '@dcc/rules/app/inventoryService.ts';
import { applyChanges } from '@dcc/rules/rules/characterChanges.ts';
import type { CustomItemEditorState } from '@dcc/rules/ui/customItemEditorModel.ts';
import { prepareCustomItemEditorDraft } from '@dcc/rules/ui/customItemEditorModel.ts';
import { inventoryCatalogue } from './inventoryHotlist.ts';

/**
 * UI state for the one missing custom-Item capability: authoring the Attack
 * Skill that a NEW physical weapon actually uses.
 *
 * This stays out of the persisted Item shape on purpose. The durable mechanics
 * are the ordinary custom Skill + AttackProfile that the Attack tab already
 * understands, and the Item merely links to that Attack through weaponAttackId.
 */
export interface CustomWeaponAttackState {
  style: 'melee' | 'ranged';
  /** Empty means use the Item name. */
  attackName: string;
  startingRank: string;
  toHitStat: StatId;
  damageDice: string;
  damageStat: StatId | null;
  damageType: DamageType;
  hands: 1 | 2;
  /** Ranged only. Empty means no authored numeric range. */
  rangeFeet: string;
  /** Optional extra base damage dice once the Skill reaches Rank 5. */
  rank5AddedDamageDice: string;
}

export function defaultCustomWeaponAttackState(): CustomWeaponAttackState {
  return {
    style: 'melee',
    attackName: '',
    startingRank: '1',
    toHitStat: 'str',
    damageDice: '1d6',
    damageStat: 'str',
    damageType: 'slashing',
    hands: 1,
    rangeFeet: '',
    rank5AddedDamageDice: '',
  };
}

export type CreateCustomItemForCrawlerResult =
  | { ok: true; item: Item; note: string }
  | { ok: false; problems: string[]; itemCreated: Item | null };

function inventoryFailure(outcome: Awaited<ReturnType<typeof addItem>>): string[] {
  if (outcome.ok) return [];
  if ('message' in outcome) return [outcome.message];
  return outcome.problems.map((problem) => problem.message);
}

function customWeaponDraft(
  itemState: CustomItemEditorState,
  state: CustomWeaponAttackState,
): CustomSkillDraft | { problem: string } {
  const startingRank = Number(state.startingRank.trim());
  if (!Number.isInteger(startingRank) || startingRank < 1) {
    return { problem: 'Weapon Skill Rank must be a whole number of at least 1.' };
  }

  let rangeFeet: number | undefined;
  if (state.style === 'ranged' && state.rangeFeet.trim() !== '') {
    rangeFeet = Number(state.rangeFeet.trim());
    if (!Number.isInteger(rangeFeet) || rangeFeet < 1) {
      return { problem: 'Ranged weapon range must be a whole number of feet of at least 1.' };
    }
  }

  const attackName = state.attackName.trim() || itemState.name.trim();
  return {
    name: attackName,
    kind: state.style === 'melee' ? 'melee-attack' : 'ranged-attack',
    commonStat: state.toHitStat,
    description: itemState.description,
    attack: {
      toHitStat: state.toHitStat,
      baseDamage: {
        dice: state.damageDice.trim(),
        stat: state.damageStat,
        type: state.damageType,
      },
      range: state.style === 'melee'
        ? { kind: 'melee' }
        : rangeFeet === undefined
          ? null
          : { feet: rangeFeet },
      aiFavor: null,
      limitations: [],
      cooldown: null,
      area: null,
      rank5: {
        addedDamageDice: state.rank5AddedDamageDice.trim() === ''
          ? null
          : state.rank5AddedDamageDice.trim(),
        notes: [],
      },
      weaponUse: { kind: 'held-weapon', hands: state.hands },
    },
  };
}

const PREVIEW_WEAPON_SKILL_ID = 'custom:weapon-preview';

/**
 * Creates one player-authored Item definition and immediately gives the open
 * crawler one copy.
 *
 * When `weapon` is supplied, the operation also authors a normal custom Attack
 * Skill/Profile and adds its starting Rank to the crawler in the SAME Character
 * save that adds the Item. The Item then points at that Attack with the ordinary
 * `weaponAttackId` relationship. Nothing in the Attack/Damage engine is
 * duplicated here.
 *
 * The reusable Skill and Item definitions are separate stored documents. As in
 * the pre-existing custom Item flow, if the final Character write is refused,
 * the definitions remain safely available in the player's catalogue rather
 * than being silently deleted.
 */
export async function createCustomItemAndAddOne(
  services: AppServices,
  character: Character,
  runtime: RuntimeState,
  state: CustomItemEditorState,
  now: () => string = () => new Date().toISOString(),
  weapon: CustomWeaponAttackState | null = null,
  quantity = 1,
): Promise<CreateCustomItemForCrawlerResult> {
  if (!Number.isInteger(quantity) || quantity < 1 || quantity > 99) return { ok: false, itemCreated: null, problems: ['Enter a whole number from 1 to 99 copies.'] };
  const loadedItems = await services.customItems.load();
  if (loadedItems.status === 'unreadable') {
    return {
      ok: false,
      itemCreated: null,
      problems: loadedItems.problems.length > 0
        ? loadedItems.problems
        : ['Your custom Item library cannot be read, so nothing was created.'],
    };
  }

  let characterForItem = character;
  let itemState = state;
  let weaponNote = '';

  if (weapon !== null) {
    if (state.slot !== 'hands' || state.handUse !== 'held') {
      return {
        ok: false,
        itemCreated: null,
        problems: ['A custom weapon must be a held Hands/Holding Item.'],
      };
    }

    const draftOrProblem = customWeaponDraft(state, weapon);
    if ('problem' in draftOrProblem) {
      return { ok: false, itemCreated: null, problems: [draftOrProblem.problem] };
    }
    const skillDraft = draftOrProblem;

    // Validate the definition and crawler Rank BEFORE persisting either custom
    // definition, so ordinary input mistakes cannot leave behind partial data.
    const skillProblems = checkCustomSkillDraft(skillDraft, contentRegistry.allSkills(), null);
    if (skillProblems.length > 0) {
      return {
        ok: false,
        itemCreated: null,
        problems: skillProblems.map((problem) => problem.message),
      };
    }

    const startingRank = Number(weapon.startingRank.trim());
    const preview = buildCustomSkillDefinition(
      skillDraft,
      PREVIEW_WEAPON_SKILL_ID,
      '1970-01-01T00:00:00.000Z',
    );

    // Validate the Item half against the preview Attack too. This catches a bad
    // Item name/modifier/placement BEFORE the custom Skill is persisted, rather
    // than leaving an orphan definition because the second half of the form was
    // invalid.
    const previewItemState: CustomItemEditorState = {
      ...state,
      slot: 'hands',
      handUse: 'held',
      weaponChoice: 'linked',
      weaponAttackId: PREVIEW_WEAPON_SKILL_ID,
    };
    const itemProbe = prepareCustomItemEditorDraft(
      previewItemState,
      contentRegistry.allItems(),
      null,
      (id) => id === PREVIEW_WEAPON_SKILL_ID ? (preview.attack ?? undefined) : contentRegistry.getAttack(id),
      (id) => id === PREVIEW_WEAPON_SKILL_ID ? preview.skill : contentRegistry.getSkill(id),
    );
    if (!itemProbe.ok) {
      return {
        ok: false,
        itemCreated: null,
        problems: itemProbe.problems.map((problem) => problem.message),
      };
    }

    const rankProbe = applyChanges(
      character,
      [{
        change: { kind: 'add-skill', skillId: PREVIEW_WEAPON_SKILL_ID, rank: startingRank },
        source: 'manual-edit',
        reason: 'Preview custom weapon Skill Rank',
      }],
      {
        now: '1970-01-01T00:00:00.000Z',
        lookupSkill: (id) => id === PREVIEW_WEAPON_SKILL_ID ? preview.skill : contentRegistry.getSkill(id),
      },
    );
    if (!rankProbe.ok) {
      return {
        ok: false,
        itemCreated: null,
        problems: rankProbe.problems.map((problem) => problem.message),
      };
    }

    const loadedSkills = await services.customSkills.load();
    if (loadedSkills.status === 'unreadable') {
      return {
        ok: false,
        itemCreated: null,
        problems: loadedSkills.problems.length > 0
          ? loadedSkills.problems
          : ['Your custom Skill library cannot be read, so the weapon Attack was not created.'],
      };
    }

    const createdSkill = await createCustomSkill(loadedSkills.library, skillDraft, {
      customSkills: services.customSkills,
      customBackgrounds: services.customBackgrounds,
      customItems: services.customItems,
      characters: services.repository,
      now,
    });
    if (!createdSkill.ok) {
      return {
        ok: false,
        itemCreated: null,
        problems: createdSkill.problems.map((problem) => problem.message),
      };
    }

    const ranked = applyChanges(
      character,
      [{
        change: { kind: 'add-skill', skillId: createdSkill.skill.id, rank: startingRank },
        source: 'manual-edit',
        reason: `Created weapon Attack ${createdSkill.skill.name}`,
      }],
      { now: now(), lookupSkill: (id) => contentRegistry.getSkill(id) },
    );
    if (!ranked.ok) {
      return {
        ok: false,
        itemCreated: null,
        problems: [
          `Created the ${createdSkill.skill.name} Attack definition, but its Rank could not be added to this crawler.`,
          ...ranked.problems.map((problem) => problem.message),
        ],
      };
    }

    characterForItem = ranked.character;
    itemState = {
      ...state,
      slot: 'hands',
      handUse: 'held',
      weaponChoice: 'linked',
      weaponAttackId: createdSkill.skill.id,
    };
    weaponNote = `${createdSkill.skill.name} Rank ${startingRank}; ${weapon.damageDice} ${weapon.damageType} base damage.`;
  }

  const prepared = prepareCustomItemEditorDraft(
    itemState,
    contentRegistry.allItems(),
    null,
    (id) => contentRegistry.getAttack(id),
    (id) => contentRegistry.getSkill(id),
  );
  if (!prepared.ok) {
    return { ok: false, itemCreated: null, problems: prepared.problems.map((problem) => problem.message) };
  }

  const created = await createCustomItem(loadedItems.library, prepared.draft, {
    customItems: services.customItems,
    customSkills: services.customSkills,
    customBackgrounds: services.customBackgrounds,
    now,
  });
  if (!created.ok) {
    return { ok: false, itemCreated: null, problems: created.problems.map((problem) => problem.message) };
  }

  const added = await addItem(characterForItem, created.item.id, quantity, {
    characters: services.repository,
    catalogue: inventoryCatalogue,
    now,
    runtime,
  });
  if (!added.ok) {
    return {
      ok: false,
      itemCreated: created.item,
      problems: [
        `Created ${created.item.name}, but it could not be added to this crawler.`,
        ...inventoryFailure(added),
        'The Item definition is safe in your catalogue; use Add known Item after resolving the problem.',
      ],
    };
  }

  return {
    ok: true,
    item: created.item,
    note: weapon === null
      ? `Created ${created.item.name} and added ${quantity} to Inventory.`
      : `Created ${created.item.name}, added ${quantity} to Inventory, and added its Attack Skill. ${weaponNote}`,
  };
}
